import { gql } from '@apollo/client';

/* ── attribution: by sector (Brinson) and by factor ─────────── */

/**
 * Brinson attribution. `securityGroupId` picks the bucketing — the id of the
 * "Major Sectors" group gives a sector view, "Asset Allocation" an asset-class
 * view; resolve it with GET_SECURITY_GROUPS below.
 *
 * Shared by the query and by `calculateReturnAttribution`, which answers with
 * the same payload — the runner renders one component either way, so the two
 * selections must not drift.
 *
 * `mode` says what the engine could actually do: `portfolio` and `mf` are full
 * look-through, while `mf_sector_nav` and `mf_nav` mean the underlyings could
 * not be priced and the allocation/selection effects are zero by construction
 * rather than by measurement. `message` explains every such fallback in words,
 * which is why the screen prints it verbatim.
 *
 * `urlId` is the handle the backend files a successful run under for 24 hours
 * (`getAnalysisResult`); it is null when the run failed. The same field is
 * selected on every analysis the agent can run (Brinson, multifactor, risk,
 * scenario, scorecard), and the agent's report puts it first so the desk can
 * name that exact run on a later turn.
 *
 * Built from plain fragments rather than by editing one string into another,
 * so `scripts/validate-graphql.js` — which resolves `${NAME}` and nothing
 * else — can check both documents against the live schema.
 */
const RESULT_HEAD = `
  success
  message
  urlId
  mode
  portfolioSymbol
  portfolioName
  benchmarkSymbol
  benchmarkName
  startDate
  endDate
  frequency
`;

const ROW_FIELDS = `
    period sector portWeight portReturn benchWeight benchReturn
    activeReturn allocationEffect selectionEffect totalEffect
`;

/** A bucket's per-stock drill-down. */
const SECURITY_FIELDS = `
      securitySymbol portWeight benchWeight portReturn benchReturn
      contribution allocationContribution selectionContribution totalContribution
`;

/* The same rows with the two fields only a backend that serves the index
   names a fund does not own has — see RETURN_ATTRIBUTION_WITH_INDEX. */
const SECURITY_FIELDS_WITH_INDEX = `${SECURITY_FIELDS} securityName held`;

const PERIOD_TOTALS = `
  periodTotals {
    period portReturn benchReturn activeReturn
    allocationEffect selectionEffect totalEffect
  }
`;

export const RETURN_ATTRIBUTION_RESULT = `
  ${RESULT_HEAD}
  rows { ${ROW_FIELDS} securities { ${SECURITY_FIELDS} } }
  ${PERIOD_TOTALS}
`;

const RETURN_ATTRIBUTION_RESULT_WITH_INDEX = `
  ${RESULT_HEAD}
  rows { ${ROW_FIELDS} securities { ${SECURITY_FIELDS_WITH_INDEX} } }
  ${PERIOD_TOTALS}
`;

/**
 * The same attribution with the benchmark constituents the fund does NOT own
 * served by name inside each bucket (`held: false`), plus a display name for
 * every stock. Kept as its own document so only the screen that asks for it
 * depends on the `includeNotHeld` argument — a backend that predates it rejects
 * the whole document, and the hook falls back to the plain one.
 */
export const RETURN_ATTRIBUTION_WITH_INDEX = gql`
  query ReturnAttributionWithIndex(
    $portfolioSymbol: String!
    $securityGroupId: Int!
    $benchmarkSymbol: String
    $startDate: String
    $endDate: String
    $frequency: String = "monthly"
  ) {
    getReturnAttribution(
      portfolioSymbol: $portfolioSymbol
      securityGroupId: $securityGroupId
      benchmarkSymbol: $benchmarkSymbol
      startDate: $startDate
      endDate: $endDate
      frequency: $frequency
      includeNotHeld: true
    ) { ${RETURN_ATTRIBUTION_RESULT_WITH_INDEX} }
  }
`;

export const RETURN_ATTRIBUTION = gql`
  query ReturnAttribution(
    $portfolioSymbol: String!
    $securityGroupId: Int!
    $benchmarkSymbol: String
    $startDate: String
    $endDate: String
    $frequency: String = "monthly"
  ) {
    getReturnAttribution(
      portfolioSymbol: $portfolioSymbol
      securityGroupId: $securityGroupId
      benchmarkSymbol: $benchmarkSymbol
      startDate: $startDate
      endDate: $endDate
      frequency: $frequency
    ) { ${RETURN_ATTRIBUTION_RESULT} }
  }
`;

/** Bucket definitions for attribution — "Major Sectors", "Asset Allocation", … */
export const GET_SECURITY_GROUPS = gql`
  query GetSecurityGroups {
    getSecurityGroups { id name description isActive isClassificationAggregation }
  }
`;

/**
 * The desk's benchmark register, for the attribution runner's benchmark picker.
 *
 * `securitySymbol` is what the attribution API wants — not the benchmark's id —
 * so rows without one cannot be run against and the screen drops them.
 * Requires a signed-in session; without one the resolver answers 401 rather
 * than an empty list, which is why the picker keeps working off the portfolio's
 * own policy benchmark when this comes back empty.
 */
export const GET_BENCHMARKS = gql`
  query GetBenchmarks($securityGroupId: Int, $isActive: Boolean) {
    getBenchmarks(securityGroupId: $securityGroupId, isActive: $isActive) {
      id
      name
      description
      isActive
      securitySymbol
    }
  }
`;

/**
 * Stored Brinson runs, and the windows that have none yet.
 *
 * `getReturnAttribution` above computes a series on demand for one bucketing;
 * this is the *report table* behind it — one stored row per horizon × bucketing,
 * each carrying its own totals, its group breakdown and the `computedAt` that
 * says how stale it is. Windows the engine could serve but has never computed
 * come back under `pending`; `computeBrinsonAttribution` is what fills them in.
 *
 * Units are not advertised by the resolver and differ by level:
 *   · row `portfolioReturn` / `benchmarkReturn` / `activeReturn` and every
 *     `*Effect` are FRACTIONS — -0.0397 is -3.97%
 *   · per-group `portfolioReturn` / `benchmarkReturn` are already PERCENTAGES
 *     — 22.7205 is 22.72%
 *   · weights are fractions at both levels
 * `useAttributionReports` converts both to percentages once, at that seam, so
 * no screen multiplies twice.
 */
const BRINSON_RESULT = `
  success
  status
  message
  stored {
    id
    portfolioSymbol
    benchmarkSymbol
    securityGroupId
    dimension
    fromDate
    toDate
    portfolioReturn
    benchmarkReturn
    activeReturn
    fullBrinson
    computedAt
    totals { allocationEffect selectionEffect interactionEffect totalActiveReturn }
    groups {
      group portfolioWeight benchmarkWeight activeWeight
      portfolioReturn benchmarkReturn
      allocationEffect selectionEffect interactionEffect totalEffect
    }
  }
  pending { horizon securityGroupId dimension fromDate toDate }
`;

/** Valid horizons, as the resolver spells them. Anything else is rejected by name. */
export const BRINSON_ATTRIBUTION = gql`
  query BrinsonAttribution(
    $portfolioSymbol: String!
    $benchmarkSymbol: String
    $horizons: [String!]
    $securityGroupIds: [Int!]
    $asOfDate: Date
  ) {
    getBrinsonAttribution(
      portfolioSymbol: $portfolioSymbol
      benchmarkSymbol: $benchmarkSymbol
      horizons: $horizons
      securityGroupIds: $securityGroupIds
      asOfDate: $asOfDate
    ) { ${BRINSON_RESULT} }
  }
`;

/** The most recent stored run for a subject, whatever window it was cut for. */
export const LATEST_BRINSON_ATTRIBUTION = gql`
  query LatestBrinsonAttribution($portfolioSymbol: String!, $benchmarkSymbol: String) {
    getLatestBrinsonAttribution(
      portfolioSymbol: $portfolioSymbol
      benchmarkSymbol: $benchmarkSymbol
    ) { ${BRINSON_RESULT} }
  }
`;

/**
 * The desk's saved report definitions — which model, buckets, period and
 * frequency each named report runs at. Requires a signed-in session; without
 * one the resolver answers `success: false, "Authentication required"` rather
 * than erroring, so the screen shows that as a state instead of a crash.
 */
export const ATTRIBUTION_SETTINGS = gql`
  query AttributionSettings($limit: Int! = 50, $includeInactive: Boolean! = false) {
    getAllAttributionSettings(limit: $limit, includeInactive: $includeInactive) {
      success
      message
      settings {
        id
        name
        description
        attributionModel
        securityGroupIds
        timePeriod
        frequency
        isOndemand
        isActive
        securityGroups { id name }
      }
    }
  }
`;

/**
 * Multifactor attribution against the broad market factor index — the same
 * read path the admin console uses. The benchmark is never an argument.
 *
 * Where the Brinson runs above bucket a fund by sector, this decomposes the
 * active return by *factor*, and it is a factor model rather than a Brinson
 * one. There is no allocation / selection / interaction split, and asking for
 * those fields is now a schema error: a factor return is the same number on
 * both sides of the comparison, so the only thing a factor can measure is the
 * tilt. One effect per factor:
 *
 *     factorEffect = (factorWeight − benchmarkWeight) / 100 × factorReturn
 *
 * `factorWeight` and `benchmarkWeight` are the two sides' EXPOSURES, Σ w·β,
 * served ×100 — a loading of 0.87 arrives as 87.0. They are not portfolio
 * weights and do not sum to 100 down the factor column, because one stock
 * loads on several factors at once.
 *
 * `factorReturn` is the raw market return for the market factor and a
 * market-excess spread for the style factors.
 *
 * `residualReturn` is everything the tilts do not explain — stock selection,
 * cash, the expense ratio, unresolved leaves and model error, served as one
 * number and never zero-filled. It replaces the old `specificReturn`.
 * factorTotal + residualReturn reconciles to activeReturn exactly.
 *
 * A single-day window serves that day as stored; a longer one Carino-links the
 * daily effects, so the identity holds over the compounded window too.
 *
 * Every numeric field arrives already in PERCENT — effects, returns, exposures
 * and weights alike. Nothing read from here is ever multiplied by 100.
 */
const MULTIFACTOR_CELL = `
  factor
  factorWeight
  benchmarkWeight
  factorReturn
  factorEffect
`;

/**
 * `holdings` is every name in the UNION of the fund and the index: the fund's
 * look-through stocks, plus each index constituent it does not hold, carried
 * as its own row at weight 0 and a negative tilt. So the holding effects sum
 * EXACTLY to the factor roll-up above them, with no unexplained remainder.
 *
 * A holding's `residualReturn` is its own share of the residual: its active
 * weight times its return, less what its tilts explain.
 */
/**
 * The same factor attribution for a PORTFOLIO rather than a scheme.
 *
 * `getMultifactorAttribution` is a separate resolver from the mutual-fund one
 * and keys on `portfolioSymbol`, but answers the identical shape — so the hook
 * maps it into the same object and the same panel renders either.
 */
export const PORTFOLIO_MULTIFACTOR_ATTRIBUTION = gql`
  query GetPortfolioMultifactorAttribution($portfolioSymbol: String!, $startDate: String, $endDate: String) {
    getMultifactorAttribution(
      portfolioSymbol: $portfolioSymbol
      startDate: $startDate
      endDate: $endDate
    ) {
      success
      message
      portfolioSymbol
      benchmarkSymbol
      startDate
      endDate
      daysLinked
      benchmarkReturn
      activeReturn
      factorTotal
      residualReturn
      factors { ${MULTIFACTOR_CELL} }
      holdings {
        securitySymbol
        name
        weight
        benchmarkWeight
        residualReturn
        factors { ${MULTIFACTOR_CELL} }
      }
    }
  }
`;

export const MF_MULTIFACTOR_ATTRIBUTION = gql`
  query GetMfMultifactorAttribution($securitySymbol: String!, $startDate: String, $endDate: String) {
    getMfMultifactorAttribution(
      securitySymbol: $securitySymbol
      startDate: $startDate
      endDate: $endDate
    ) {
      success
      message
      securitySymbol
      benchmarkSymbol
      startDate
      endDate
      daysLinked
      benchmarkReturn
      activeReturn
      factorTotal
      residualReturn
      factors { ${MULTIFACTOR_CELL} }
      holdings {
        securitySymbol
        name
        weight
        benchmarkWeight
        residualReturn
        factors { ${MULTIFACTOR_CELL} }
      }
    }
  }
`;
